import { ChartingItemType } from '@caresend/types';
import { isNullish } from '@caresend/utils';
import update from 'immutability-helper';
import cloneDeep from 'lodash.clonedeep';

/**
 * Using an array of indices, recursively looks for nested group ChartingItem
 * children to find a single non-group ChartingItem.
 *
 * Example: [1, 2] - Root ChartingItem of index 1, nested non-group
 * ChartingItem of index 2.
 */
const getChartingItemByIndexArray = (chartingItems, indexArray) => {
    if (!chartingItems)
        return;
    const deepItemArray = indexArray.reduce((prevItems, currentItemIndex, index) => {
        const currentItem = prevItems[currentItemIndex];
        if (!currentItem)
            throw new Error('Missing charting item');
        if (index === indexArray.length - 1) {
            return [currentItem];
        }
        if (currentItem.type === ChartingItemType.GROUP && currentItem.children) {
            return currentItem.children;
        }
        throw new Error('Expected group ChartingItem');
    }, chartingItems);
    const item = deepItemArray[0];
    return item;
};
const getChartingItemHighlightReferenceByIndexArray = (chartingHighlightItems, indexArray) => {
    if (!chartingHighlightItems)
        return;
    const deepItemArray = indexArray.reduce((prevItems, currentItemIndex, index) => {
        const currentItem = prevItems[currentItemIndex];
        if (!currentItem)
            throw new Error('Missing charting highlight item');
        if (index === indexArray.length - 1) {
            return [currentItem];
        }
        if (currentItem.children) {
            return currentItem.children;
        }
        throw new Error('Expected ChartingItemHighlightReference with children');
    }, chartingHighlightItems);
    const item = deepItemArray[0];
    return item;
};
/**
 * Returns a new list of charting items, with one item changed at the root
 * level of ChartingItems[]
 */
const getUpdatedChartingItemsForRootItem = (chartingItems, indexArray, newPartialChartingItem) => {
    const rootIndex = indexArray[0];
    if (isNullish(rootIndex))
        throw new Error('First element missing in indexArray');
    if (indexArray.length > 1)
        throw new Error('Expected single item in indexArray');
    return update(chartingItems, {
        [rootIndex]: {
            $merge: newPartialChartingItem,
        },
    });
};
/**
 * Returns a new list of charting items, with one item changed at an item
 * nested in a group ChartingItem. Only supports 1 level of nesting.
 */
const getUpdatedChartingItemsForNestedItem = (chartingItems, indexArray, newPartialChartingItem) => {
    if (indexArray.length !== 2)
        throw new Error('Expected two items in indexArray');
    const newChartingItems = cloneDeep(chartingItems);
    const firstIndex = indexArray[0];
    const secondIndex = indexArray[1];
    if (firstIndex === undefined || secondIndex === undefined)
        throw new Error('Missing index');
    const group = newChartingItems[firstIndex];
    if (!group)
        throw new Error('Missing group');
    const { children } = group;
    if (!children)
        throw new Error('Missing children');
    children[secondIndex] = {
        ...children[secondIndex],
        ...newPartialChartingItem,
    };
    return newChartingItems;
};
/**
 * Returns a new list of charting items, with one item changed.
 */
const getUpdatedChartingItems = (chartingItems, indexArray, newPartialChartingItem) => {
    if (indexArray.length === 1) {
        return getUpdatedChartingItemsForRootItem(chartingItems, indexArray, newPartialChartingItem);
    }
    return getUpdatedChartingItemsForNestedItem(chartingItems, indexArray, newPartialChartingItem);
};

export { getChartingItemByIndexArray, getChartingItemHighlightReferenceByIndexArray, getUpdatedChartingItems };
